import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';

interface Size {
  name: string;
  stock_quantity: number;
  price?: number | null;
}

interface Variant {
  id: number;
  color: string;
  color_code?: string | null;
  price?: number | null;
  is_active: boolean;
  sizes: Size[];
}

interface Product {
  id: number;
  name: string;
  product_code: string;
  price: number;
  promotional_price?: number | null;
  product_images?: string[];
  variants: Variant[];
}

interface LineItem {
  key: string; // variant_id + size
  variant_id: number;
  product_name: string;
  color: string;
  size: string;
  unit_price: number;
  quantity: number;
  max_stock: number;
}

const getImageUrl = (image?: string | null) => {
  if (!image) return null;
  if (image.startsWith('http')) return image;
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
  return `${baseUrl}/storage/${image}`;
};

const effectiveUnitPrice = (product: Product, variant: Variant, size: Size): number => {
  const base = size.price ?? variant.price ?? product.price;
  const promo = product.promotional_price;
  return promo && promo < base ? promo : base;
};

const AdminManualOrder: React.FC = () => {
  const navigate = useNavigate();

  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');

  // Order-level
  const [deliveryFee, setDeliveryFee] = useState('0');
  const [discount, setDiscount] = useState('0');
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid'>('pending');
  const [notes, setNotes] = useState('');

  // Line items
  const [items, setItems] = useState<LineItem[]>([]);

  // Product picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Fetch products only while the picker is open, debounced on the search term.
  // Kept dependency-light (no useCallback in deps) so unrelated re-renders of
  // this large form never re-trigger the fetch or churn the modal.
  useEffect(() => {
    if (!pickerOpen) return;

    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setLoadingProducts(true);
        const res = await apiService.get('/admin/products', {
          params: { search, per_page: 30 },
        });
        if (!cancelled) setProducts(res.data || res.products || res || []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [pickerOpen, search]);

  const openPicker = () => {
    setSelectedProduct(null);
    setSelectedVariant(null);
    setLoadingProducts(true); // show the loader immediately, before the debounce fires
    setPickerOpen(true);
  };

  const addLine = (product: Product, variant: Variant, size: Size) => {
    const key = `${variant.id}-${size.name}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        // bump quantity, capped at stock
        return prev.map((i) =>
          i.key === key
            ? { ...i, quantity: Math.min(i.quantity + 1, i.max_stock) }
            : i
        );
      }
      return [
        ...prev,
        {
          key,
          variant_id: variant.id,
          product_name: product.name,
          color: variant.color,
          size: size.name,
          unit_price: effectiveUnitPrice(product, variant, size),
          quantity: 1,
          max_stock: size.stock_quantity,
        },
      ];
    });
    // Reset variant selection but keep product for adding more sizes
    setSelectedVariant(null);
  };

  const updateQty = (key: string, qty: number) => {
    setItems((prev) =>
      prev.map((i) =>
        i.key === key ? { ...i, quantity: Math.max(1, Math.min(qty, i.max_stock)) } : i
      )
    );
  };

  const removeLine = (key: string) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  };

  const subtotal = items.reduce((sum, i) => sum + i.unit_price * i.quantity, 0);
  const deliveryNum = Number(deliveryFee) || 0;
  const discountNum = Number(discount) || 0;
  const total = Math.max(0, subtotal - discountNum + deliveryNum);

  const canSubmit =
    customerName.trim() !== '' &&
    customerPhone.trim() !== '' &&
    items.length > 0 &&
    !submitting;

  const handleSubmit = async () => {
    setError('');
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await apiService.createManualOrder({
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        customer_email: customerEmail.trim() || undefined,
        customer_address: customerAddress.trim() || undefined,
        items: items.map((i) => ({
          variant_id: i.variant_id,
          size: i.size,
          quantity: i.quantity,
        })),
        delivery_fee: deliveryNum,
        discount_amount: discountNum,
        payment_status: paymentStatus,
        notes: notes.trim() || undefined,
      });
      navigate('/admin/orders', {
        state: { created: res.order_number },
      });
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to create order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 min-w-0">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin/orders')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Orders
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create Manual Order</h1>
        <p className="text-gray-600">For sales made over social media. Stock is reduced automatically.</p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: customer + items */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Customer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="text"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="09..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Optional"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
                <textarea
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Township, city, full address..."
                />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Items</h2>
              <button
                type="button"
                onClick={openPicker}
                className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 text-sm rounded-md hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4" />
                Add product
              </button>
            </div>

            {items.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">No items yet. Click "Add product".</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {items.map((i) => (
                  <div key={i.key} className="flex items-center gap-3 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{i.product_name}</p>
                      <p className="text-xs text-gray-500">
                        {i.color} / {i.size} · {i.unit_price.toLocaleString()} MMK
                        <span className="text-gray-400"> (max {i.max_stock})</span>
                      </p>
                    </div>
                    <input
                      type="number"
                      min={1}
                      max={i.max_stock}
                      value={i.quantity}
                      onChange={(e) => updateQty(i.key, Number(e.target.value))}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                    />
                    <div className="w-28 text-right text-sm font-medium text-gray-900">
                      {(i.unit_price * i.quantity).toLocaleString()}
                    </div>
                    <button
                      onClick={() => removeLine(i.key)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: summary */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6 lg:sticky lg:top-24">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">{subtotal.toLocaleString()} MMK</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Delivery fee</span>
                <input
                  type="number"
                  min={0}
                  value={deliveryFee}
                  onChange={(e) => setDeliveryFee(e.target.value)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Discount</span>
                <input
                  type="number"
                  min={0}
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                />
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-3 text-base font-bold">
                <span>Total</span>
                <span>{total.toLocaleString()} MMK</span>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment status</label>
              <select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value as 'pending' | 'paid')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Optional (e.g. Facebook order ref)"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="mt-6 w-full bg-indigo-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create Order'}
            </button>
          </div>
        </div>
      </div>

      {/* Product picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                {selectedProduct ? selectedProduct.name : 'Select a product'}
              </h3>
              <button
                onClick={() => setPickerOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto">
              {!selectedProduct ? (
                <>
                  <div className="relative mb-4">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      autoFocus
                      placeholder="Search products..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="min-h-[320px]">
                  {loadingProducts ? (
                    <div className="flex items-center justify-center py-16 text-gray-500">
                      <svg className="animate-spin h-5 w-5 mr-2 text-indigo-500" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Loading products...
                    </div>
                  ) : products.length === 0 ? (
                    <p className="text-center text-gray-500 py-16">No products found.</p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {products.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setSelectedProduct(p);
                            setSelectedVariant(p.variants?.[0] ?? null);
                          }}
                          className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-gray-50 px-2 rounded"
                        >
                          {p.product_images?.[0] && (
                            <img
                              src={getImageUrl(p.product_images[0]) || undefined}
                              className="w-9 h-9 rounded object-cover flex-shrink-0"
                              alt=""
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                            <p className="text-xs text-gray-500 font-mono">{p.product_code}</p>
                          </div>
                          <span className="text-sm text-gray-700">{Number(p.price).toLocaleString()}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setSelectedProduct(null);
                      setSelectedVariant(null);
                    }}
                    className="text-sm text-indigo-600 hover:text-indigo-800 mb-4 inline-flex items-center gap-1"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to products
                  </button>

                  {/* Colors */}
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Color</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedProduct.variants
                      .filter((v) => v.is_active)
                      .map((v) => (
                        <button
                          key={v.id}
                          onClick={() => setSelectedVariant(v)}
                          className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 border ${
                            selectedVariant?.id === v.id
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {v.color_code && (
                            <span
                              className="w-4 h-4 rounded-full border border-gray-300"
                              style={{ backgroundColor: v.color_code }}
                            />
                          )}
                          {v.color}
                        </button>
                      ))}
                  </div>

                  {/* Sizes */}
                  {selectedVariant && (
                    <>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">Size &amp; stock</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {selectedVariant.sizes.map((s) => {
                          const out = s.stock_quantity <= 0;
                          return (
                            <button
                              key={s.name}
                              disabled={out}
                              onClick={() => addLine(selectedProduct, selectedVariant, s)}
                              className={`p-3 rounded-lg text-left border transition ${
                                out
                                  ? 'border-gray-200 opacity-40 cursor-not-allowed'
                                  : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-gray-900">{s.name}</span>
                                <span
                                  className={`text-xs px-2 py-0.5 rounded ${
                                    out ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                  }`}
                                >
                                  {s.stock_quantity}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {effectiveUnitPrice(selectedProduct, selectedVariant, s).toLocaleString()} MMK
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManualOrder;
